import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import {
  Box,
  EMPTY_ARRAY,
  FormControlSizes,
  FormGroup,
  HStack,
  isMultileg,
  MaxOrderSizeDialogs,
  MixpanelEvent,
  MixpanelEventSource,
  type OrderIntent,
  type SideEnum,
  UnifiedLiquidityEnum,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
} from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useState } from 'react';
import { useDisplaySettings } from '../../../../../providers/DisplaySettingsProvider';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import type { OrderBookPanelConfig, SingleSymbolPanelConfig } from '../../../../../types/LayoutConfig';
import { PriceIncrement } from '../../../../Ladder/PriceIncrement';
import { Panel } from '../../../../Layout/Components/Panel';
import { useHandleClickMarketPrice } from '../../../../MarketDataCard/useHandleClickMarketPrice';
import { OrderBook } from '../../../../OrderBook/OrderBook';
import { SingleSymbolPanelHeader } from './SingleSymbolPanelHeader';
import { SingleSymbolPanelSettings } from './SingleSymbolPanelSettings';

export function OrderBookPanel({ api, params }: IDockviewPanelProps<OrderBookPanelConfig>) {
  const mixpanel = useMixpanel();
  const [showSettings, setShowSettings] = useState(false);
  const { clickToTradeAll } = useTradingSettings();
  const { searchableSecurities, securitiesBySymbol } = useSecuritiesContext();
  const { getOrderCurrency } = useGetOrderCurrency();
  const {
    symbol,
    marketAccounts,
    markets,
    aggregation,
    showCumQty = false,
    priceIncrement,
    customTitle,
    settingsInitialOpen,
    subAccount,
    orderPresetID,
  } = params;

  const security = useSecurity(symbol);
  const selectedMarkets = markets ?? EMPTY_ARRAY;
  const selectedMarketAccounts = marketAccounts ?? EMPTY_ARRAY;
  const {
    clickToTradePreviewOrder,
    onClickPrice: handleClickPrice,
    onCancelActive: handleCancelActive,
    maxOrderSizeDialogs,
  } = useHandleClickMarketPrice({
    symbol,
    subAccount,
    orderPresetID,
    marketAccounts: selectedMarketAccounts,
    markets: selectedMarkets,
    aggregation,

    unifiedLiquidity: UnifiedLiquidityEnum.Disabled,
    defaultSubAccountId: undefined,
  });

  // Open settings panel when the panel is first added and `settingsInitialOpen` is true
  useEffect(() => {
    if (settingsInitialOpen) {
      setShowSettings(true);
      api.updateParameters({ settingsInitialOpen: false });
    }
  }, [settingsInitialOpen, api]);

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      if (!customTitle) {
        api.setTitle(`${symbol ? (securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol) : 'Order'} Book`);
      }
      api.updateParameters({ symbol, marketAccounts: [], markets: [], aggregation: undefined });
    },
    [api, securitiesBySymbol, customTitle]
  );

  const handleRowClick = useCallback(
    ({ price, side, quantity, intent }: { price: string; side: SideEnum; quantity: string; intent: OrderIntent }) => {
      handleClickPrice({
        values: {
          currency: getOrderCurrency(security, side),
          price,
          side,
          orderQty: quantity,
          intent,
        },
        source: MixpanelEventSource.OrderBook,
        activateOrder: true,
      });
    },
    [handleClickPrice, security, getOrderCurrency]
  );

  const handleCumQtyClick = useCallback(() => {
    api.updateParameters({ showCumQty: !showCumQty });
  }, [api, showCumQty]);

  const handleUpdateParameters = useCallback(
    (params: Partial<SingleSymbolPanelConfig>) => {
      api.updateParameters(params);
    },
    [api]
  );

  const handlePriceIncrementChange = useCallback(
    (index: number, newIncrement: string) => {
      mixpanel.track(MixpanelEvent.ChangePriceBucketing);
      api.updateParameters({ priceIncrement: newIncrement });
    },
    [api, mixpanel]
  );

  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();

  return (
    <>
      <Panel
        panelApi={api}
        position="relative"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        data-testid="order-book-panel"
      >
        <SingleSymbolPanelHeader
          security={security}
          aggregation={aggregation}
          markets={markets}
          marketAccounts={marketAccounts}
          enableUnifiedLiquidity={false}
          orderPresetID={orderPresetID}
          subAccount={subAccount}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          panelTypeLabel="Order Book"
        />
        {showSettings ? (
          <SingleSymbolPanelSettings
            onClose={() => setShowSettings(false)}
            onSymbolChange={handleSymbolChange}
            onUpdateParameters={handleUpdateParameters}
            availableSecurities={searchableSecurities}
            marketAccounts={marketAccounts}
            enableUnifiedLiquidity={false}
            allowUnifiedLiquidity={false}
            orderPresetID={orderPresetID}
            aggregation={aggregation}
            subAccount={subAccount}
            allowMultileg={false}
            markets={markets}
            symbol={symbol}
            customSettings={
              <FormGroup
                label="Price Increment"
                inline
                justifyContent="space-between"
                data-testid="price-increment-form-group"
              >
                <Box background="backgroundDefaultButton" borderRadius="borderRadiusDefault">
                  <PriceIncrement
                    min={security?.MinPriceIncrement ?? '0.01'}
                    value={priceIncrement ?? security?.DefaultPriceIncrement ?? '0.01'}
                    onChange={handlePriceIncrementChange}
                    size={FormControlSizes.Small}
                  />
                </Box>
              </FormGroup>
            }
          />
        ) : security ? (
          <OrderBook
            security={security}
            selectedMarketAccounts={selectedMarketAccounts}
            selectedMarkets={selectedMarkets}
            priceIncrement={priceIncrement ?? security.DefaultPriceIncrement}
            showPriceIncrementSetting={true}
            showCumQty={showCumQty}
            clickToTradePreviewOrder={clickToTradePreviewOrder}
            onIncrementChange={handlePriceIncrementChange}
            hideLastTraded={isMultileg(security)}
            onRowClick={handleRowClick}
            onMouseLeaveRow={handleCancelActive}
            onCumQtyClick={handleCumQtyClick}
            showAllInPrices={showAllInPrices}
            showFirmLiquidity={showFirmLiquidity}
            enableClickToTrade={clickToTradeAll}
          />
        ) : (
          <HStack flex="1">No symbol selected</HStack>
        )}
      </Panel>
      <MaxOrderSizeDialogs {...maxOrderSizeDialogs} />
    </>
  );
}
